/**
 * Semantic token definitions for light and dark themes (DESIGN.md-aligned).
 *
 * Light is the canonical theme spec'd by DESIGN.md (pure white canvas, gray-900
 * ink, Brand Blue #0B00F3). Dark is a derived companion theme that preserves
 * the same palette identity but inverts surfaces / shifts shade indices so
 * accents read on dark backgrounds.
 *
 * @see ../../DESIGN.md
 */

import { palette, extendedPalette, CHART_SERIES_PALETTE } from "./palette"

// ── Token shape ──────────────────────────────────────────────

export interface ColorRoleTokens {
  main: string
  hover: string
  active: string
  disabled: string
  foreground: string
  subtle: string
  subtleForeground: string
  border: string
}

export interface BadgeColorTokens {
  main: string
  subtle: string
  subtleForeground: string
  border: string
}

export interface ThemeTokens {
  // Brand color roles
  primary: ColorRoleTokens
  secondary: ColorRoleTokens

  // Feedback color roles
  error: ColorRoleTokens
  warning: ColorRoleTokens
  success: ColorRoleTokens
  info: ColorRoleTokens

  // AI accent (DESIGN.md `{colors.ai}` family)
  ai: ColorRoleTokens
  aiFillLight: string   // 4% mint — panel backgrounds for AI sections
  aiFillMedium: string  // 10% mint — button backgrounds for secondary AI actions
  aiFillChip: string    // 15% mint — chip/badge backgrounds for AI tags
  aiDeep: string        // #00997d — chart series differentiation
  aiText: string        // #0a8c75 — text on mint surfaces

  // Competitor (charts only)
  competitor: string

  // Surfaces
  background: string
  contentBackground: string  // main content area (below header)
  foreground: string
  card: string
  cardForeground: string
  popover: string
  popoverForeground: string
  muted: string
  mutedForeground: string

  // Borders & ring
  border: string
  borderSubtle: string
  ring: string

  // Input
  inputBorder: string
  inputBackground: string
  inputFocusBorder: string
  switchBackground: string
  switchBackgroundChecked: string

  // Tooltip
  tooltip: string
  tooltipForeground: string

  // Slate chip (DESIGN.md `{colors.slate-chip-*}`)
  slateChipBg: string
  slateChipText: string

  // Extended badge colors (legacy — kept for back-compat)
  violet: BadgeColorTokens
  orange: BadgeColorTokens
  lime: BadgeColorTokens

  // Categorical chart palette — semantic aliases onto CHART_SERIES_PALETTE.
  chartGreen: string   // #6BA84F — chart-1
  chartPurple: string  // #7C5CB0 — chart-2
  chartYellow: string  // #F2B33D — chart-3 (amber)
  chartTeal: string    // #3D9DA8 — chart-4
  chartOrange: string  // #E86A5C — chart-5 (coral)
  chartRed: string     // #C4629B — chart-6 (magenta)
  chartBlue: string    // #4F7DBF — chart-7
  chartOlive: string   // #A8A03D — chart-8

  // Chart chrome
  chartAxisText: string  // #4C5773 — axis + tick + value labels
  chartGrid: string      // #EDEDED — horizontal grid rule and axis lines

  // Chart palette (1–24: generic categorical, CHART_SERIES_PALETTE in order)
  chart1: string
  chart2: string
  chart3: string
  chart4: string
  chart5: string
  chart6: string
  chart7: string
  chart8: string
  chart9: string
  chart10: string
  chart11: string
  chart12: string
  chart13: string
  chart14: string
  chart15: string
  chart16: string
  chart17: string
  chart18: string
  chart19: string
  chart20: string
  chart21: string
  chart22: string
  chart23: string
  chart24: string

  // DESIGN.md "Chart Series Colors" — each KPI keeps the same color
  // across every chart. See DESIGN.md § Colors → Chart Series Colors.
  chartSpend: string                  // #6BA84F — chart-1 green
  chartCpa: string                    // #7C5CB0 — chart-2 purple
  chartCreativeVolume: string         // #F2B33D — chart-3 amber
  chartCreativeConcentration: string  // #E86A5C — chart-5 coral
  chartStickRate: string              // #C4629B — chart-6 magenta
  chartWinners: string                // #464B67 — neutral guide, not a series
  chartRoas: string                   // #3D9DA8 — chart-4 teal
  chartConversions: string            // #E86A5C (same as creativeConcentration)
  chartAmber: string                  // #f59e0b — generic amber for chart usage
}

// ── Shorthand references ─────────────────────────────────────

const p = palette
const ext = extendedPalette
const pastel = extendedPalette.chartPastel
const series = CHART_SERIES_PALETTE

// AI opacity-based fills, expressed as color-mix so they composite cleanly
// over the white canvas without precomputing per-surface variants.
const aiHex = ext.ai[500]
const aiFillLight = `color-mix(in srgb, ${aiHex} 4%, transparent)`
const aiFillMedium = `color-mix(in srgb, ${aiHex} 10%, transparent)`
const aiFillChip = `color-mix(in srgb, ${aiHex} 15%, transparent)`

// ── Light theme (canonical, DESIGN.md-aligned) ───────────────

export const lightTheme: ThemeTokens = {
  primary: {
    main: p.primary[600],          // #0B00F3
    hover: p.primary[700],         // #0900c4
    active: p.primary[800],
    disabled: p.neutral[300],
    foreground: "#ffffff",
    subtle: p.primary[50],
    subtleForeground: p.primary[700],
    border: p.primary[300],
  },
  secondary: {
    // No second brand. Mapped to neutrals so `bg-secondary` consumers render
    // as a muted surface. Cleanup will migrate consumers off `secondary`.
    main: p.neutral[600],
    hover: p.neutral[700],
    active: p.neutral[800],
    disabled: p.neutral[300],
    foreground: "#ffffff",
    subtle: p.neutral[100],
    subtleForeground: p.neutral[700],
    border: p.neutral[300],
  },

  error: {
    main: p.error[600],
    hover: p.error[700],
    active: p.error[800],
    disabled: p.neutral[300],
    foreground: "#ffffff",
    subtle: p.error[50],
    subtleForeground: p.error[700],
    border: p.error[300],
  },
  warning: {
    main: p.warning[600],
    hover: p.warning[700],
    active: p.warning[800],
    disabled: p.neutral[300],
    foreground: "#ffffff",
    subtle: p.warning[50],
    subtleForeground: p.warning[700],
    border: p.warning[300],
  },
  success: {
    main: p.success[600],          // #059669 — success icon
    hover: p.success[700],
    active: p.success[800],
    disabled: p.neutral[300],
    foreground: "#ffffff",
    subtle: p.success[50],         // #ecfdf5 — badge bg
    subtleForeground: p.success[700], // #047857 — badge text
    border: p.success[300],
  },
  info: {
    main: p.info[600],
    hover: p.info[700],
    active: p.info[800],
    disabled: p.neutral[300],
    foreground: "#ffffff",
    subtle: p.info[50],            // #eff6ff — status-sent badge bg
    subtleForeground: p.info[700], // #1d4ed8 — status-sent text
    border: p.info[300],
  },

  ai: {
    main: ext.ai[500],             // #00F5C8
    hover: ext.ai[600],            // #00997d
    active: ext.ai[700],
    disabled: p.neutral[300],
    foreground: ext.ai[900],
    subtle: aiFillChip,
    subtleForeground: ext.ai[700], // #0a8c75 (ai-text)
    border: ext.ai[500],
  },
  aiFillLight,
  aiFillMedium,
  aiFillChip,
  aiDeep: ext.ai[600],             // #00997d
  aiText: ext.ai[700],             // #0a8c75

  competitor: ext.competitor[500], // #6C4BFF

  background: "#ffffff",           // DESIGN.md canvas — pure white
  contentBackground: "rgba(236, 236, 240, 0.30)",
  foreground: p.neutral[900],      // #111827 — ink
  card: "#ffffff",
  cardForeground: p.neutral[900],
  popover: "#ffffff",
  popoverForeground: p.neutral[900],
  muted: p.neutral[50],            // #f9fafb — surface-soft
  mutedForeground: p.neutral[500], // #6b7280

  border: p.neutral[200],          // #e5e7eb
  borderSubtle: p.neutral[100],    // #f3f4f6
  ring: p.primary[600],

  inputBorder: p.neutral[300],     // #d1d5db
  inputBackground: "#ffffff",
  inputFocusBorder: p.primary[600],
  switchBackground: p.neutral[300],
  switchBackgroundChecked: p.primary[600],

  tooltip: p.neutral[900],
  tooltipForeground: "#ffffff",

  slateChipBg: ext.slateChip.bg,     // #EEEEF4
  slateChipText: ext.slateChip.text, // #464B67

  violet: {
    main: ext.violet[500],
    subtle: ext.violet[100],
    subtleForeground: ext.violet[700],
    border: ext.violet[500],
  },
  orange: {
    main: ext.orange[500],
    subtle: ext.orange[100],
    subtleForeground: ext.orange[700],
    border: ext.orange[500],
  },
  lime: {
    main: ext.lime[500],
    subtle: ext.lime[100],
    subtleForeground: ext.lime[700],
    border: ext.lime[500],
  },

  chartGreen: pastel.green,
  chartPurple: pastel.purple,
  chartYellow: pastel.amber,
  chartTeal: pastel.teal,
  chartOrange: pastel.coral,
  chartRed: pastel.magenta,
  chartBlue: pastel.blue,
  chartOlive: pastel.olive,

  chartAxisText: ext.chartChrome.axisText,  // #4C5773
  chartGrid: ext.chartChrome.grid,          // #EDEDED

  chart1: series[0],
  chart2: series[1],
  chart3: series[2],
  chart4: series[3],
  chart5: series[4],
  chart6: series[5],
  chart7: series[6],
  chart8: series[7],
  chart9: series[8],
  chart10: series[9],
  chart11: series[10],
  chart12: series[11],
  chart13: series[12],
  chart14: series[13],
  chart15: series[14],
  chart16: series[15],
  chart17: series[16],
  chart18: series[17],
  chart19: series[18],
  chart20: series[19],
  chart21: series[20],
  chart22: series[21],
  chart23: series[22],
  chart24: series[23],

  // DESIGN.md Chart Series Colors — locked to KPI.
  chartSpend: series[0],
  chartCpa: series[1],
  chartCreativeVolume: series[2],
  chartCreativeConcentration: series[4],
  chartStickRate: series[5],
  chartWinners: ext.slateChip.text,              // #464B67 — guide line, stays neutral
  chartRoas: series[3],
  chartConversions: series[4],
  chartAmber: p.warning[500],                    // #f59e0b
}

// ── Dark theme (derived companion) ───────────────────────────
//
// Designed as a deliberate dark theme, not a mechanical inversion. Key
// moves vs. an automatic shade-flip:
//   • Canvas lifted off pure black (#0a0a0b) for tier separation on LCD.
//   • Three real surface tiers — canvas / card / popover — each ~6% lighter
//     than the prior, so cards visibly contain themselves.
//   • Borders decoupled from surfaces (one step lighter than `card`) so they
//     never disappear into the background they sit on.
//   • Brand Blue preserved as blue (not the indigo-purple a naive lift to
//     primary[400] produces). DESIGN.md identity stays recognizable.
//   • Semantic subtles use 950-tier shades so error/warning/success badges
//     read calm against the canvas instead of vibrating.
//   • Chart series overrides limited to the colors that *fail* on dark
//     (Winners, axis ink, grid rule). Everything else keeps cross-mode
//     consistency.

// Off-palette dark-mode surface tiers. Hand-tuned for ~6% lightness deltas
// so depth is perceptible without high-contrast harshness.
const darkCanvas = "#0a0a0b"
const darkCard = "#16171a"
const darkPopover = "#1c1d21"
const darkBorder = "#2a2b30"

// Dark-mode brand blue. p.primary[400] (#5e54ff) reads purple-indigo at
// lower saturation; this value preserves the Brand Blue identity from
// DESIGN.md while remaining legible on dark backgrounds.
const darkPrimary = "#5b6cff"
const darkPrimaryHover = "#7d8aff"
const darkPrimaryActive = "#a8b1ff"
const darkPrimarySubtle = "#1a1d3a"

// Dark-mode chart axis ink — keeps the #4C5773 slate hue while clearing
// contrast against darkCanvas.
const darkChartAxisText = "#c3c8da"

// AI fills on dark surfaces — slightly higher opacity to compensate for
// the darker base; mint stays the same hue.
const aiFillLightDark = `color-mix(in srgb, ${aiHex} 8%, transparent)`
const aiFillMediumDark = `color-mix(in srgb, ${aiHex} 16%, transparent)`
const aiFillChipDark = `color-mix(in srgb, ${aiHex} 22%, transparent)`

export const darkTheme: ThemeTokens = {
  primary: {
    main: darkPrimary,             // blue-preserving brand (not purple-leaning indigo)
    hover: darkPrimaryHover,
    active: darkPrimaryActive,
    disabled: darkBorder,
    foreground: darkCanvas,
    subtle: darkPrimarySubtle,
    subtleForeground: darkPrimaryActive,
    border: p.primary[500],
  },
  secondary: {
    main: p.neutral[400],
    hover: p.neutral[300],
    active: p.neutral[200],
    disabled: darkBorder,
    foreground: darkCanvas,
    subtle: darkCard,
    subtleForeground: p.neutral[300],
    border: darkBorder,
  },

  error: {
    main: p.error[400],
    hover: p.error[300],
    active: p.error[200],
    disabled: darkBorder,
    foreground: darkCanvas,
    subtle: p.error[950],          // calmer than 900; less "vibrating red" on dark
    subtleForeground: p.error[300],
    border: p.error[800],
  },
  warning: {
    main: p.warning[400],
    hover: p.warning[300],
    active: p.warning[200],
    disabled: darkBorder,
    foreground: darkCanvas,
    subtle: p.warning[950],
    subtleForeground: p.warning[300],
    border: p.warning[800],
  },
  success: {
    main: p.success[400],
    hover: p.success[300],
    active: p.success[200],
    disabled: darkBorder,
    foreground: darkCanvas,
    subtle: p.success[950],
    subtleForeground: p.success[300],
    border: p.success[800],
  },
  info: {
    main: p.info[400],
    hover: p.info[300],
    active: p.info[200],
    disabled: darkBorder,
    foreground: darkCanvas,
    subtle: p.info[950],
    subtleForeground: p.info[300],
    border: p.info[800],
  },

  ai: {
    main: ext.ai[400],
    hover: ext.ai[300],
    active: ext.ai[200],
    disabled: darkBorder,
    foreground: darkCanvas,
    subtle: aiFillChipDark,
    subtleForeground: ext.ai[300],
    border: ext.ai[700],
  },
  aiFillLight: aiFillLightDark,
  aiFillMedium: aiFillMediumDark,
  aiFillChip: aiFillChipDark,
  aiDeep: ext.ai[500],
  aiText: ext.ai[300],

  competitor: ext.competitor[400],

  background: darkCanvas,          // lifted off pure black
  contentBackground: "transparent",
  foreground: p.neutral[100],
  card: darkCard,                  // tier 2 — clearly elevated above canvas
  cardForeground: p.neutral[100],
  popover: darkPopover,            // tier 3 — floats above card
  popoverForeground: p.neutral[100],
  muted: darkPopover,
  mutedForeground: p.neutral[400],

  border: darkBorder,              // visibly distinct from surfaces
  borderSubtle: darkCard,          // subtle but not invisible on canvas
  ring: darkPrimary,

  inputBorder: darkBorder,
  inputBackground: darkCard,
  inputFocusBorder: darkPrimary,
  switchBackground: darkBorder,
  switchBackgroundChecked: darkPrimary,

  tooltip: p.neutral[100],
  tooltipForeground: p.neutral[900],

  slateChipBg: ext.slateChip.text,   // invert chip surfaces for dark
  slateChipText: ext.slateChip.bg,

  violet: {
    main: ext.violet[500],
    subtle: ext.violet[900],
    subtleForeground: ext.violet[100],
    border: ext.violet[700],
  },
  orange: {
    main: ext.orange[500],
    subtle: ext.orange[900],
    subtleForeground: ext.orange[100],
    border: ext.orange[700],
  },
  lime: {
    main: ext.lime[500],
    subtle: ext.lime[900],
    subtleForeground: ext.lime[100],
    border: ext.lime[700],
  },

  chartGreen: pastel.green,
  chartPurple: pastel.purple,
  chartYellow: pastel.amber,
  chartTeal: pastel.teal,
  chartOrange: pastel.coral,
  chartRed: pastel.magenta,
  chartBlue: pastel.blue,
  chartOlive: pastel.olive,

  chartAxisText: darkChartAxisText, // override: #4C5773 is ~2.1:1 on darkCanvas
  chartGrid: darkBorder,            // override: #EDEDED is a glaring rule on dark

  chart1: series[0],
  chart2: series[1],
  chart3: series[2],
  chart4: series[3],
  chart5: series[4],
  chart6: series[5],
  chart7: series[6],
  chart8: series[7],
  chart9: series[8],
  chart10: series[9],
  chart11: series[10],
  chart12: series[11],
  chart13: series[12],
  chart14: series[13],
  chart15: series[14],
  chart16: series[15],
  chart17: series[16],
  chart18: series[17],
  chart19: series[18],
  chart20: series[19],
  chart21: series[20],
  chart22: series[21],
  chart23: series[22],
  chart24: series[23],

  chartSpend: series[0],
  chartCpa: series[1],
  chartCreativeVolume: series[2],
  chartCreativeConcentration: series[4],
  chartStickRate: series[5],
  chartWinners: "#a8acc4",         // override: slate #464B67 is ~1.8:1 on darkCanvas
  chartRoas: series[3],
  chartConversions: series[4],
  chartAmber: p.warning[500],
}

/**
 * Chart series colors keyed by KPI name. The single source of truth for
 * chart color choices — every Recharts wrapper must read from here so the
 * "same color across every chart" rule from DESIGN.md is preserved.
 */
export const chartSeries = {
  spend: lightTheme.chartSpend,
  cpa: lightTheme.chartCpa,
  creativeVolume: lightTheme.chartCreativeVolume,
  creativeConcentration: lightTheme.chartCreativeConcentration,
  stickRate: lightTheme.chartStickRate,
  winners: lightTheme.chartWinners,
  roas: lightTheme.chartRoas,
  conversions: lightTheme.chartConversions,
} as const

export type ChartSeriesKey = keyof typeof chartSeries

/**
 * Categorical chart palette for non-KPI categorical visualizations
 * (e.g., topic diversification, creative themes). Derived from the
 * brand + extended palette so charts stay in the design-system family
 * even when the data isn't a named KPI series.
 *
 * Use cyclically (`chartCategorical[i % chartCategorical.length]`).
 */
export const chartCategorical: readonly string[] = [...CHART_SERIES_PALETTE]
